'use client';

import { type FC } from 'react';
import { SkinImage } from '../../components/ui/SkinImage';
import { useQuery } from '@tanstack/react-query';
import { ScreenContent, ScreenFixedTop } from '../../screen';
import { ErrorMessage } from '../../components/ErrorMessage';
import { UserAvatar } from '../../components/UserAvatar/UserAvatar';
import { useApiClient } from '../../contexts/ApiContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { queryKeys } from '../../lib/queryClient';
import { TypeEnum1 } from '../../services/generated/Api';
import type { ScreenParams } from '../../types/navigation';
import translations from './DrawsHistoryScreen.translations';

interface DrawsHistoryScreenProps {
  params?: ScreenParams;
}

function displayName(firstName?: string | null, lastName?: string | null): string {
  return [firstName, lastName].filter(Boolean).join(' ') || 'Anonymous';
}

export const DrawsHistoryScreen: FC<DrawsHistoryScreenProps> = ({ params }) => {
  const { t } = useLocalTranslation(translations);
  const apiClient = useApiClient();
  const navigation = useNavigation();

  const drawType = params?.drawType as TypeEnum1 | undefined;

  const handleUserClick = (userId: string) => {
    navigation.navigate('profile', { userId });
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('timeJustNow');
    if (diffMins < 60) return t('timeMinutesAgo', { count: diffMins });
    if (diffHours < 24) return t('timeHoursAgo', { count: diffHours });
    if (diffDays < 7) return t('timeDaysAgo', { count: diffDays });

    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
    });
  };

  const {
    data: winners = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.drawsHistory(drawType),
    queryFn: () =>
      apiClient.api.drawsControllerGetWinnersHistory({
        limit: 20,
        type: drawType,
      }),
  });

  if (error && winners.length === 0) {
    return (
      <>
        <ScreenFixedTop title={t('title')} />
        <ScreenContent className="flex-1">
          <ErrorMessage
            error={error instanceof Error ? error.message : t('loadError')}
            onRetry={() => refetch()}
          />
        </ScreenContent>
      </>
    );
  }

  return (
    <>
      <ScreenFixedTop title={t('title')} />
      <ScreenContent className="flex-1">
        {isLoading && winners.length === 0 ? (
          <div className="px-5 py-10 text-center text-base text-white/60">{t('loading')}</div>
        ) : winners.length === 0 ? (
          <div className="px-5 py-10 text-center text-base text-white/60">{t('empty')}</div>
        ) : (
          <div className="flex flex-col gap-2 p-4">
            {winners.map((winner) => (
              <button
                key={winner.id}
                type="button"
                onClick={() => handleUserClick(winner.winner.id)}
                className="flex w-full items-center gap-3 rounded-2xl bg-fill-tertiary px-3.5 py-3 text-left transition active:opacity-80"
              >
                <UserAvatar
                  userId={winner.winner.id}
                  firstName={winner.winner.firstName}
                  lastName={winner.winner.lastName}
                  level={winner.winner.level}
                  size="m"
                />
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="truncate text-base font-semibold text-label-primary">
                    {displayName(winner.winner.firstName, winner.winner.lastName)}
                  </div>
                  <div className="mt-0.5 truncate text-sm text-label-secondary">{winner.prizeItemName}</div>
                  <div className="mt-0.5 truncate text-xs text-label-tertiary">{formatDate(winner.completedAt)}</div>
                </div>
                <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-fill-secondary">
                  <SkinImage name={winner.prizeItemName} src={winner.prizeItemImage} className="absolute inset-0" imgClassName="h-full w-full object-contain p-0.5" />
                </div>
              </button>
            ))}
          </div>
        )}
      </ScreenContent>
    </>
  );
};
